var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Factory from './Factory';
// Manages the game state.
class Game {
    constructor(p5) {
        this.components = [];
        this.isPaused = false;
        this.angle = 0;
        this.maxAngle = Math.PI / 4;
        this.angleVelocity = 0;
        this.zVelocity = -Game.DEFAULT_Z_VELOCITY * Game.EASING_FACTOR;
        this.xFactor = 1.0;
        this.fFactor = 1.0;
        this.zFactor = 1.0;
        this.directionMap = {
            ArrowUp: -Game.DEFAULT_Z_VELOCITY,
            ArrowDown: 0,
        };
        this.rotateMap = {
            ArrowRight: -Game.DEFAULT_Y_ANGLE,
            ArrowLeft: Game.DEFAULT_Y_ANGLE,
        };
        this.p5 = p5;
        p5.setup = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.settings = (yield this.p5.loadJSON('game.json'));
            this.maxAngle = (_a = this.settings.maxAngle) !== null && _a !== void 0 ? _a : Math.PI / 4;
            this.p5.frameRate(this.settings.fps);
            console.debug('Game Settings:', this.settings);
            this.onWindowResize();
            // Initialize components
            const factory = new Factory(this);
            factory.addStars();
            factory.addFighter();
            console.info(`Game "${this.settings.name}" initialized.`);
        });
        p5.draw = () => {
            // Update all components if the game is not paused
            if (!this.isPaused) {
                const nextAngle = this.angle + this.angleVelocity;
                if (nextAngle <= this.maxAngle && nextAngle >= -this.maxAngle) {
                    this.angle = nextAngle;
                }
                this.components.forEach((component) => component.update());
            }
            // Render all components, skipping those not visible
            this.p5.background(0);
            this.components.forEach((component) => {
                if (component.visible)
                    component.render();
            });
        };
        p5.keyPressed = () => {
            const key = this.p5.key;
            console.debug(`Key pressed: ${key}`);
            if (key === this.settings.pauseKey) {
                this.isPaused = !this.isPaused;
                console.info(`Game ${this.isPaused ? 'paused' : 'resumed'}.`);
                return;
            }
            if (key === this.settings.centerKey) {
                this.angle = 0;
                console.info('View centered.');
                return;
            }
            // Apply velocity on cursor key press
            if (key in this.directionMap) {
                this.zVelocity = this.directionMap[key];
            }
            else if (key in this.rotateMap) {
                this.angleVelocity = this.rotateMap[key];
            }
            // Notify all components of the key press
            this.components.forEach((component) => component.keyPressed(key));
        };
        p5.keyReleased = () => {
            const key = this.p5.key;
            console.debug(`Key released: ${key}`);
            if (key in this.directionMap) {
                // Smoothly slow down instead of stopping abruptly
                this.zVelocity *= Game.EASING_FACTOR;
            }
            else if (key in this.rotateMap) {
                // Stop rotation immediately for better control
                this.angleVelocity = 0;
            }
            // Notify all components of the key release
            this.components.forEach((component) => component.keyReleased(key));
        };
        p5.windowResized = () => {
            this.onWindowResize();
        };
    }
    onWindowResize() {
        var _a, _b, _c;
        this.p5.resizeCanvas(this.p5.windowWidth, this.p5.windowHeight);
        // Calculate aspect ratio
        this.xFactor = this.p5.width / this.p5.height;
        // Calculate field of view factor
        const fov = (_a = this.settings.fieldOfView) !== null && _a !== void 0 ? _a : Game.DEFAULT_FOV;
        const f = 1 / Math.tan((fov * (Math.PI / 180)) / 2);
        this.fFactor = f;
        // Calculate z factor for depth scaling
        const zFar = (_b = this.settings.zFar) !== null && _b !== void 0 ? _b : Game.DEFAULT_Z_FAR;
        const zNear = (_c = this.settings.zNear) !== null && _c !== void 0 ? _c : Game.DEFAULT_Z_NEAR;
        const zDistance = zFar - zNear;
        const zScale = zFar / zDistance;
        this.zFactor = zScale - (zFar * zNear) / zDistance;
    }
    // Converts world coordinates (-1..1) to screen coordinates (0..width/height).
    toScreen(p, ignoreXFactor = false) {
        const rotated = this.rotateAroundY(p, this.angle);
        const projected = this.project(rotated, ignoreXFactor);
        const x = ((projected.x + 1) * this.p5.width) / 2;
        const y = (1 - (projected.y + 1) / 2) * this.p5.height;
        return this.p5.createVector(x, y);
    }
    rotate(v, angleX, angleY, angleZ) {
        let rotated = this.rotateAroundX(v, angleX);
        rotated = this.rotateAroundY(rotated, angleY);
        rotated = this.rotateAroundZ(rotated, angleZ);
        return rotated;
    }
    rotateAroundX(v, angle) {
        const c = this.p5.cos(angle);
        const s = this.p5.sin(angle);
        const ry = v.y * c - v.z * s;
        const rz = v.y * s + v.z * c;
        return this.p5.createVector(v.x, ry, rz);
    }
    rotateAroundY(v, angle) {
        const c = this.p5.cos(angle);
        const s = this.p5.sin(angle);
        const rx = v.x * c - v.z * s;
        const rz = v.x * s + v.z * c;
        return this.p5.createVector(rx, v.y, rz);
    }
    rotateAroundZ(v, angle) {
        const c = this.p5.cos(angle);
        const s = this.p5.sin(angle);
        const rx = v.x * c - v.y * s;
        const ry = v.x * s + v.y * c;
        return this.p5.createVector(rx, ry, v.z);
    }
    project(p, ignoreXFactor = false) {
        const x = ignoreXFactor
            ? p.x * this.fFactor
            : p.x * this.xFactor * this.fFactor;
        const y = p.y * this.fFactor;
        const z = p.z * this.zFactor;
        if (p.z !== 0) {
            return this.p5.createVector(x / p.z, y / p.z, z / p.z);
        }
        else {
            return this.p5.createVector(x, y, z);
        }
    }
}
Game.DEFAULT_Z_VELOCITY = 0.01;
Game.DEFAULT_Y_ANGLE = 0.01;
Game.DEFAULT_Z_FAR = 1000;
Game.DEFAULT_Z_NEAR = 0.1;
Game.DEFAULT_FOV = 90;
Game.EASING_FACTOR = 0.1;
export default Game;
